// src/hooks/usePaystack.ts
import { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { paystackService } from "../services/paystack.service";
import {
  paymentStateMachine,
  PaymentState,
} from "../services/payment-state.service";
import { useAuth } from "./useAuth";
import { PaymentErrorHandler } from "../utils/payment-errors";

interface UsePaystackReturn {
  loading: boolean;
  paymentState: PaymentState;
  error: string | null;
  initializePayment: (
    cardId: string,
    email: string,
    bouquetAmount?: number,
  ) => Promise<void>;
  verifyPayment: (reference: string) => Promise<any>;
  resetPayment: () => void;
}

export const usePaystack = (): UsePaystackReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentState, setPaymentState] = useState<PaymentState>("idle");
  const navigate = useNavigate();
  const { user } = useAuth();

  // Set navigate and user in paystack service
  useEffect(() => {
    paystackService.setNavigate(navigate);
  }, [navigate]);

  // Subscribe to payment state changes
  useEffect(() => {
    const unsubscribe = paymentStateMachine.subscribe(setPaymentState);
    return unsubscribe;
  }, []);

  const initializePayment = useCallback(
    async (cardId: string, email: string, bouquetAmount?: number) => {
      if (!user?.id) {
        setError("User not authenticated");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        await paystackService.initializePayment({
          cardId,
          email,
          userId: user.id,
          bouquetAmount,
        });
      } catch (err) {
        const handledError = PaymentErrorHandler.handle(err);
        setError(handledError.message);
      } finally {
        setLoading(false);
      }
    },
    [user],
  );

  const verifyPayment = useCallback(async (reference: string) => {
    setLoading(true);
    setError(null);

    try {
      const result = await paystackService.verifyPayment(reference);
      return result;
    } catch (err) {
      const handledError = PaymentErrorHandler.handle(err);
      setError(handledError.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const resetPayment = useCallback(() => {
    paystackService.reset();
    setError(null);
  }, []);

  return {
    loading,
    paymentState,
    error,
    initializePayment,
    verifyPayment,
    resetPayment,
  };
};
